import * as React from "react";
import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { useLineLosses, type LineLoss } from "@/features/lines/hooks";
import { useActiveRun } from "@/features/production/hooks";
import { formatQuantity } from "@/lib/quantity-format";

export interface LossesCardProps {
  lineId: string;
}

const TYPE_LABEL: Record<LineLoss["lossType"], string> = {
  WASTE: "Desperdício",
  REWORK: "Retrabalho",
  REJECT: "Refugo",
};

export function LossesCard({ lineId }: LossesCardProps): React.ReactElement {
  const lossesQuery = useLineLosses(lineId);
  const { data: losses = [], isLoading } = lossesQuery;
  const { data: run } = useActiveRun(lineId);

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="bg-bg-muted h-20 rounded motion-safe:animate-pulse-dot" />
      </Card>
    );
  }

  if (lossesQuery.isError) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.QueryError error={lossesQuery.error} onRetry={() => void lossesQuery.refetch()} />
      </Card>
    );
  }

  // Spec 081 — quantidade aceita fração. `Number()` explícito porque o valor atravessa o wire e um
  // `+` sobre string concatenaria em vez de somar.
  const total = losses.reduce((s, l) => s + Number(l.quantity), 0);

  if (losses.length === 0) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.Empty
          title="Sem perdas registradas"
          description="Use 'Registrar dado → Perda' no card de produção para lançar."
        />
      </Card>
    );
  }

  const targetQty = run?.plannedQty ?? 0;
  const pctLabel = targetQty > 0 ? `${((total / targetQty) * 100).toFixed(1)}% da meta` : null;

  return (
    <Card className="space-y-3 rounded-2xl px-[22px] py-5" aria-labelledby="losses-card-h2">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2
          id="losses-card-h2"
          className="text-[14px] font-semibold tracking-tight text-text-primary"
        >
          Perdas registradas
        </h2>
        <div className="flex items-baseline gap-2 text-[11px] text-text-tertiary">
          <span className="font-mono text-[14px] font-medium text-text-primary">
            {formatQuantity(total)} un
          </span>
          {pctLabel ? <span>· {pctLabel}</span> : null}
        </div>
      </header>

      <ul className="space-y-1.5">
        {losses.slice(0, 10).map((l) => (
          <li key={l.id} className="flex items-start justify-between gap-2 text-[12px]">
            <div className="min-w-0">
              <span className="font-medium text-text-primary">{TYPE_LABEL[l.lossType]}</span>
              {/* Spec 082 — o produto distingue três perdas de vácuo em gramaturas diferentes,
                  que sem ele apareceriam como a mesma linha repetida. */}
              {l.productName ? (
                <span className="ml-1.5 text-text-primary">{l.productName}</span>
              ) : null}
              <span aria-hidden>:</span>
              {l.reason ? (
                <span className="ml-1.5 text-text-secondary">{l.reason}</span>
              ) : (
                <span className="ml-1.5 text-text-tertiary">Sem mais detalhes registrados.</span>
              )}
            </div>
            <span className="whitespace-nowrap font-mono text-text-primary">
              {formatQuantity(l.quantity)} un
            </span>
          </li>
        ))}
      </ul>

      {losses.length > 10 ? (
        <p className="text-[11px] text-text-tertiary">
          + {losses.length - 10} lançamento(s) adicional(is).
        </p>
      ) : null}
    </Card>
  );
}
